import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, FolderTree, Plus, Trash2, Search, Users, Package, X, Clock, Building2, UserCog } from 'lucide-react';
import { Link } from 'react-router-dom';

// Managers' admin for folders: a hierarchy above deliveries, per client Account.
// A folder groups items (TOTP / passkeys / passwords) and grants timed access to
// members. All calls hit the session-authenticated vault router.
const vault = (path, opts = {}) =>
  fetch(`/api/passkey-vault${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });

const TYPE_BADGE = {
  TOTP: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  Passkey: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  Password: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
};

const fmtExpiry = (iso) => {
  if (!iso) return 'Illimité';
  const d = new Date(iso);
  if (d < new Date()) return `Expiré (${d.toLocaleDateString('fr-FR')})`;
  return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const FoldersPage = ({ isManager = false, manageAll = false }) => {
  const [folders, setFolders] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // delegations (real managers only)
  const [delegations, setDelegations] = useState([]);
  const [dEmail, setDEmail] = useState('');
  const [dUntil, setDUntil] = useState('');
  const [dGlobal, setDGlobal] = useState(true);          // global vs a set of folders
  const [dFolders, setDFolders] = useState(new Set());   // selected folder ids when not global

  // create-folder form
  const [newName, setNewName] = useState('');
  const [accountQuery, setAccountQuery] = useState('');
  const [accountResults, setAccountResults] = useState([]);
  const [pickedAccount, setPickedAccount] = useState(null);

  // selected-folder detail
  const [items, setItems] = useState([]);        // items in the folder
  const [allItems, setAllItems] = useState([]);  // everything a manager can file
  const [members, setMembers] = useState([]);
  const [tab, setTab] = useState('items');       // 'items' | 'members'

  // add-item picker
  const [itemQuery, setItemQuery] = useState('');
  const [pickedItems, setPickedItems] = useState(new Set());

  // add-member form
  const [mEmail, setMEmail] = useState('');
  const [mExpiry, setMExpiry] = useState('');
  const [mAllItems, setMAllItems] = useState(true);
  const [mItems, setMItems] = useState(new Set());

  const selected = folders.find((f) => f.id === selectedId) || null;

  const loadFolders = useCallback(async () => {
    setLoading(true);
    try {
      const r = await vault('/folders');
      if (!r.ok) throw new Error('Chargement des dossiers impossible');
      setFolders(await r.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadFolders(); }, [loadFolders]);

  const loadDelegations = useCallback(async () => {
    if (!isManager) return;
    try {
      const r = await vault('/delegations');
      if (r.ok) setDelegations(await r.json());
    } catch { /* delegation optional */ }
  }, [isManager]);

  useEffect(() => { loadDelegations(); }, [loadDelegations]);

  const saveDelegation = async () => {
    const email = dEmail.trim().toLowerCase();
    if (!email.endsWith('@solganeo.com')) { setError('Un email @solganeo.com est requis'); return; }
    const until = dUntil ? new Date(dUntil).toISOString() : null;
    // global = one row (folderId null); otherwise one row per selected folder.
    const targets = dGlobal ? [null] : [...dFolders];
    if (!dGlobal && !targets.length) { setError('Choisissez au moins un dossier'); return; }
    setError('');
    for (const folderId of targets) {
      const r = await vault('/delegations', { method: 'POST', body: JSON.stringify({ email, until, folderId }) });
      if (!r.ok) { setError('Délégation impossible'); return; }
    }
    setDEmail(''); setDUntil(''); setDGlobal(true); setDFolders(new Set());
    await loadDelegations();
  };
  const toggleDFolder = (id) => setDFolders((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const removeDelegation = async (id) => {
    const r = await vault(`/delegations/${id}`, { method: 'DELETE' });
    if (!r.ok) { setError('Retrait impossible'); return; }
    await loadDelegations();
  };

  const loadDetail = useCallback(async (id) => {
    if (!id) return;
    try {
      const [it, all, mem] = await Promise.all([
        vault(`/folders/${id}/items`).then((r) => r.json()),
        vault('/manageable-items').then((r) => r.json()),
        vault(`/folders/${id}/members`).then((r) => r.json()),
      ]);
      setItems(Array.isArray(it) ? it : []);
      setAllItems(Array.isArray(all) ? all : []);
      setMembers(Array.isArray(mem) ? mem : []);
    } catch (e) { setError('Chargement du dossier impossible'); }
  }, []);

  useEffect(() => { if (selectedId) loadDetail(selectedId); }, [selectedId, loadDetail]);

  // account search (debounced)
  useEffect(() => {
    if (!accountQuery.trim()) { setAccountResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await vault(`/accounts?q=${encodeURIComponent(accountQuery)}`);
        if (r.ok) setAccountResults(await r.json());
      } catch { /* ignore */ }
    }, 350);
    return () => clearTimeout(t);
  }, [accountQuery]);

  const createFolder = async () => {
    if (!newName.trim()) return;
    setError('');
    const r = await vault('/folders', { method: 'POST', body: JSON.stringify({ name: newName.trim(), accountId: pickedAccount?.id || null }) });
    if (!r.ok) { setError('Création impossible'); return; }
    const { id } = await r.json();
    setNewName(''); setAccountQuery(''); setPickedAccount(null); setAccountResults([]);
    await loadFolders();
    setSelectedId(id);
  };

  const deleteFolder = async (id) => {
    if (!window.confirm('Supprimer ce dossier ? Les items seront détachés (pas supprimés) et tous les accès retirés.')) return;
    const r = await vault(`/folders/${id}`, { method: 'DELETE' });
    if (!r.ok) { setError('Suppression impossible'); return; }
    if (selectedId === id) setSelectedId(null);
    await loadFolders();
  };

  const attachItems = async () => {
    const ids = [...pickedItems];
    if (!ids.length) return;
    const r = await vault(`/folders/${selectedId}/items`, { method: 'POST', body: JSON.stringify({ itemIds: ids }) });
    if (!r.ok) { setError('Ajout des items impossible'); return; }
    setPickedItems(new Set());
    await loadDetail(selectedId);
  };

  const detachItem = async (itemId) => {
    const r = await vault(`/folders/${selectedId}/items/${itemId}`, { method: 'DELETE' });
    if (!r.ok) { setError('Retrait impossible'); return; }
    await loadDetail(selectedId);
  };

  const saveMember = async () => {
    const email = mEmail.trim().toLowerCase();
    if (!email.endsWith('@solganeo.com')) { setError('Un email @solganeo.com est requis'); return; }
    setError('');
    const body = {
      email,
      expiresAt: mExpiry ? new Date(mExpiry).toISOString() : null,
      allItems: mAllItems,
      items: mAllItems ? [] : [...mItems],
    };
    const r = await vault(`/folders/${selectedId}/members`, { method: 'POST', body: JSON.stringify(body) });
    if (!r.ok) { setError("Enregistrement du membre impossible"); return; }
    setMEmail(''); setMExpiry(''); setMAllItems(true); setMItems(new Set());
    await loadDetail(selectedId);
  };

  const removeMember = async (accessId) => {
    const r = await vault(`/folders/${selectedId}/members/${accessId}`, { method: 'DELETE' });
    if (!r.ok) { setError('Retrait du membre impossible'); return; }
    await loadDetail(selectedId);
  };

  // items available to attach = manageable minus those already in this folder
  const inFolder = new Set(items.map((i) => i.id));
  const attachable = allItems
    .filter((i) => !inFolder.has(i.id))
    .filter((i) => !itemQuery.trim() || (i.name || '').toLowerCase().includes(itemQuery.toLowerCase()));

  const toggle = (set, setter, id) => {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    setter(next);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 py-4">
            <Link to="/" className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" title="Retour">
              <ArrowLeft className="w-6 h-6 text-gray-500" />
            </Link>
            <div className="flex items-center gap-2">
              <FolderTree className="w-6 h-6 text-blue-500" />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dossiers</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError('')}><X className="w-4 h-4" /></button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: folder list + create */}
          <div className="lg:col-span-1 space-y-4">
            {manageAll && (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Nouveau dossier</h2>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Nom du dossier"
                className="w-full mb-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
              />
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  value={pickedAccount ? pickedAccount.name : accountQuery}
                  onChange={(e) => { setPickedAccount(null); setAccountQuery(e.target.value); }}
                  placeholder="Client (Account) — optionnel"
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                />
                {!pickedAccount && accountResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {accountResults.map((a) => (
                      <button key={a.id} onClick={() => { setPickedAccount(a); setAccountResults([]); }}
                        className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-white">
                        {a.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={createFolder} disabled={!newName.trim()}
                className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-40 transition-colors">
                <Plus className="w-4 h-4" /> Créer
              </button>
            </div>
            )}

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 text-sm font-semibold text-gray-900 dark:text-white">
                Dossiers ({folders.length})
              </div>
              {loading ? (
                <div className="p-6 text-center text-gray-500 dark:text-gray-400">Chargement…</div>
              ) : folders.length === 0 ? (
                <div className="p-6 text-center text-gray-500 dark:text-gray-400 text-sm">Aucun dossier</div>
              ) : (
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {folders.map((f) => (
                    <li key={f.id}>
                      <div className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/50 ${selectedId === f.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                        onClick={() => setSelectedId(f.id)}>
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{f.name}</div>
                          {f.accountName && <div className="text-xs text-gray-500 dark:text-gray-400">{f.accountName}</div>}
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); deleteFolder(f.id); }}
                          className="p-1 text-gray-400 hover:text-red-500" title="Supprimer">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Delegations (real managers only) */}
            {isManager && (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 mb-1">
                  <UserCog className="w-4 h-4 text-blue-500" />
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Délégations</h2>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Confier la gestion des dossiers à un non-manager, pour une période.</p>
                {delegations.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Aucune délégation.</p>
                ) : (
                  <ul className="divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg mb-3">
                    {delegations.map((d) => (
                      <li key={d.id} className="flex items-center justify-between px-3 py-2">
                        <div className="min-w-0">
                          <div className="text-sm text-gray-900 dark:text-white truncate">{d.email}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 flex-wrap">
                            <Clock className="w-3 h-3" /> {fmtExpiry(d.until)}
                            <span>· {d.folderName ? d.folderName : 'Tous les dossiers'}</span>
                          </div>
                        </div>
                        <button onClick={() => removeDelegation(d.id)} className="p-1 text-gray-400 hover:text-red-500" title="Retirer">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <input value={dEmail} onChange={(e) => setDEmail(e.target.value)} placeholder="email@solganeo.com"
                  className="w-full mb-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white" />
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Jusqu'au (vide = illimité)</label>
                <input type="datetime-local" value={dUntil} onChange={(e) => setDUntil(e.target.value)}
                  className="w-full mb-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white" />
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Portée</label>
                <label className="flex items-center gap-2 mb-2 text-sm text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={dGlobal} onChange={(e) => setDGlobal(e.target.checked)} />
                  Tous les dossiers (global)
                </label>
                {!dGlobal && (
                  <div className="mb-2 border border-gray-200 dark:border-gray-700 rounded-lg max-h-40 overflow-y-auto">
                    {folders.length === 0 ? (
                      <p className="p-2 text-xs text-gray-500 dark:text-gray-400">Aucun dossier</p>
                    ) : folders.map((f) => (
                      <label key={f.id} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/50">
                        <input type="checkbox" checked={dFolders.has(f.id)} onChange={() => toggleDFolder(f.id)} />
                        <span className="text-gray-900 dark:text-white truncate">{f.name}</span>
                      </label>
                    ))}
                  </div>
                )}
                <button onClick={saveDelegation} disabled={!dEmail.trim()}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-40">
                  <Plus className="w-4 h-4" /> Déléguer
                </button>
              </div>
            )}
          </div>

          {/* Right: selected folder detail */}
          <div className="lg:col-span-2">
            {!selected ? (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center text-gray-500 dark:text-gray-400">
                <FolderTree className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                Sélectionnez un dossier pour gérer ses items et ses membres.
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">{selected.name}</h2>
                  {selected.accountName && <p className="text-sm text-gray-500 dark:text-gray-400">Client : {selected.accountName}</p>}
                </div>

                {/* tabs */}
                <div className="flex gap-1 px-4 pt-3 border-b border-gray-200 dark:border-gray-700">
                  <button onClick={() => setTab('items')}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg ${tab === 'items' ? 'text-blue-600 border-b-2 border-blue-500' : 'text-gray-500 dark:text-gray-400'}`}>
                    <Package className="w-4 h-4" /> Items ({items.length})
                  </button>
                  <button onClick={() => setTab('members')}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg ${tab === 'members' ? 'text-blue-600 border-b-2 border-blue-500' : 'text-gray-500 dark:text-gray-400'}`}>
                    <Users className="w-4 h-4" /> Membres ({members.length})
                  </button>
                </div>

                {tab === 'items' ? (
                  <div className="p-6 space-y-6">
                    {/* items in folder */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Items du dossier</h3>
                      {items.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">Aucun item. Ajoutez-en ci-dessous.</p>
                      ) : (
                        <ul className="divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg">
                          {items.map((i) => (
                            <li key={i.id} className="flex items-center justify-between px-4 py-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className={`text-xs px-2 py-0.5 rounded-full ${TYPE_BADGE[i.type] || ''}`}>{i.type}</span>
                                <span className="text-sm text-gray-900 dark:text-white truncate">{i.name}</span>
                                {i.deliveryName && <span className="text-xs text-gray-400">· {i.deliveryName}</span>}
                              </div>
                              <button onClick={() => detachItem(i.id)} className="p-1 text-gray-400 hover:text-red-500" title="Retirer">
                                <X className="w-4 h-4" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {/* add items */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Ajouter des items</h3>
                      <div className="relative mb-2">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input value={itemQuery} onChange={(e) => setItemQuery(e.target.value)} placeholder="Rechercher un item…"
                          className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white" />
                      </div>
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-64 overflow-y-auto">
                        {attachable.length === 0 ? (
                          <p className="p-4 text-sm text-gray-500 dark:text-gray-400">Aucun item disponible</p>
                        ) : attachable.map((i) => (
                          <label key={i.id} className="flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/50">
                            <input type="checkbox" checked={pickedItems.has(i.id)} onChange={() => toggle(pickedItems, setPickedItems, i.id)} />
                            <span className={`text-xs px-2 py-0.5 rounded-full ${TYPE_BADGE[i.type] || ''}`}>{i.type}</span>
                            <span className="text-sm text-gray-900 dark:text-white truncate">{i.name}</span>
                            {i.folderId && <span className="text-xs text-orange-500">· déjà dans un autre dossier</span>}
                          </label>
                        ))}
                      </div>
                      <button onClick={attachItems} disabled={!pickedItems.size}
                        className="mt-3 flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-40">
                        <Plus className="w-4 h-4" /> Ajouter ({pickedItems.size})
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="p-6 space-y-6">
                    {/* members list */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Membres</h3>
                      {members.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">Aucun membre.</p>
                      ) : (
                        <ul className="divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg">
                          {members.map((m) => (
                            <li key={m.id} className="flex items-center justify-between px-4 py-3">
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">{m.email}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2 flex-wrap">
                                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtExpiry(m.expiresAt)}</span>
                                  <span>· {m.allItems ? 'Tous les items' : `${m.items.length} item(s)`}</span>
                                </div>
                              </div>
                              <button onClick={() => removeMember(m.id)} className="p-1 text-gray-400 hover:text-red-500" title="Retirer">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {/* add member */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Ajouter / modifier un membre</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input value={mEmail} onChange={(e) => setMEmail(e.target.value)} placeholder="email@solganeo.com"
                          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white" />
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Expire le (vide = illimité)</label>
                          <input type="datetime-local" value={mExpiry} onChange={(e) => setMExpiry(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white" />
                        </div>
                      </div>
                      <label className="flex items-center gap-2 mt-3 text-sm text-gray-700 dark:text-gray-300">
                        <input type="checkbox" checked={mAllItems} onChange={(e) => setMAllItems(e.target.checked)} />
                        Accès à tous les items du dossier
                      </label>
                      {!mAllItems && (
                        <div className="mt-2 border border-gray-200 dark:border-gray-700 rounded-lg max-h-48 overflow-y-auto">
                          {items.length === 0 ? (
                            <p className="p-3 text-sm text-gray-500 dark:text-gray-400">Ajoutez d'abord des items au dossier.</p>
                          ) : items.map((i) => (
                            <label key={i.id} className="flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/50">
                              <input type="checkbox" checked={mItems.has(i.id)} onChange={() => toggle(mItems, setMItems, i.id)} />
                              <span className={`text-xs px-2 py-0.5 rounded-full ${TYPE_BADGE[i.type] || ''}`}>{i.type}</span>
                              <span className="text-sm text-gray-900 dark:text-white truncate">{i.name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                      <button onClick={saveMember} disabled={!mEmail.trim()}
                        className="mt-3 flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-40">
                        <Plus className="w-4 h-4" /> Enregistrer le membre
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FoldersPage;
